import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useCompaniesStore, type Company } from "../../store/admin/companiesStore";
import { getCustomSelectStyles } from "../../Layouts/admin/shared";

interface SelectOption {
  value: string;
  label: string;
  /** Equipos de la compañía (companyListAll?withUnitCounts=true); undefined si no vino. */
  count?: number;
}

/**
 * Genera las opciones del selector a partir de un array de compañías.
 * Usa una lógica de label consistente en toda la app.
 */
export function buildCompanyOptions(companies: Company[]): SelectOption[] {
  return companies.map((company) => ({
    value: company.id,
    label: company.name || company.business_name || company.id,
    count: typeof company.unit_count === "number" ? company.unit_count : undefined,
  }));
}

interface CompanySelectorProps {
  /** ID de la compañía actualmente seleccionada */
  value: string | null | undefined;
  /** Callback cuando cambia la selección */
  onChange: (companyId: string | null) => void;
  /** Modo oscuro */
  isDarkMode: boolean;
  /** Permitir limpiar la selección */
  isClearable?: boolean;
  /** Mostrar indicador de error */
  hasError?: boolean;
  /** Deshabilitar el selector */
  isDisabled?: boolean;
  /** Mostrar indicador de carga */
  isLoading?: boolean;
  /** Texto de placeholder personalizado */
  placeholder?: string;
  /** Clase CSS adicional para el contenedor */
  className?: string;
  /** Compañías a mostrar (si no se pasan, usa allCompanies del store) */
  companies?: Company[];
  menuPlacement?: "auto" | "top" | "bottom";
  /** Encabezado: 32 px, borde fino y sin separador, al tono de la campana y el avatar. */
  compact?: boolean;
}

export default function CompanySelector({
  value,
  onChange,
  isDarkMode,
  isClearable = false,
  hasError = false,
  isDisabled = false,
  isLoading = false,
  placeholder,
  className,
  companies: companiesProp,
  menuPlacement = "auto",
  compact = false,
}: CompanySelectorProps) {
  const { t } = useTranslation("common");

  // Si no se pasan compañías por prop, usar las del store
  const storeCompanies = useCompaniesStore((state) => state.allCompanies);
  const storeLoading = useCompaniesStore((state) => state.allCompaniesLoading);

  const companies = companiesProp ?? storeCompanies;
  const loading = isLoading || storeLoading;
  const options = buildCompanyOptions(companies);

  const selectedOption = options.find((opt) => opt.value === value) ?? null;

  return (
    <div className={className}>
      <Select<SelectOption>
        value={selectedOption}
        onChange={(option) => onChange(option?.value ?? null)}
        options={options}
        placeholder={placeholder ?? t("header.selectCompany")}
        isSearchable
        isClearable={isClearable}
        isDisabled={isDisabled}
        isLoading={loading}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        styles={(compact ? compactStyles(isDarkMode, hasError) : getCustomSelectStyles(isDarkMode, hasError)) as any}
        noOptionsMessage={() => t("header.noCompanies")}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement={menuPlacement}
        // Equipos de cada compañía en el menú (backlog del mapa, punto 2): el
        // admin reseller sabe a cuál entrar sin abrirla. En el valor elegido no.
        formatOptionLabel={(opt, { context }) =>
          context === "menu" && opt.count !== undefined ? (
            <span className="flex items-center justify-between gap-3">
              <span className="truncate">{opt.label}</span>
              <span className="flex-shrink-0 text-xs tabular-nums opacity-60">{opt.count}</span>
            </span>
          ) : (
            opt.label
          )
        }
      />
    </div>
  );
}

// Variante del encabezado (Daniel, 30-oct): la caja de 36 px con fondo de tarjeta
// pesaba tanto como el logotipo. Mismo comportamiento, menos presencia.
function compactStyles(isDarkMode: boolean, hasError: boolean) {
  const base = getCustomSelectStyles(isDarkMode, hasError);
  const line = isDarkMode ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)";
  return {
    ...base,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: (provided: any, state: any) => ({
      ...(base.control?.(provided, state) ?? provided),
      minHeight: "32px",
      height: "32px",
      borderRadius: "10px",
      fontSize: "13px",
      backgroundColor: "transparent",
      border: `1px solid ${hasError ? "#ef4444" : line}`,
      boxShadow: state.isFocused ? `0 0 0 2px ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)"}` : "none",
      "&:hover": { borderColor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.16)" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dropdownIndicator: (provided: any) => ({ ...provided, padding: "0 8px 0 4px" }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    clearIndicator: (provided: any) => ({ ...provided, padding: "0 4px" }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    valueContainer: (provided: any, state: any) => ({ ...(base.valueContainer?.(provided, state) ?? provided), padding: "0 10px" }),
  };
}
